import { isEqual } from "lodash-es";
import { createSelector, lruMemoize } from "reselect";

import { RootState } from "@app/store/rootReducer";
import { parseApiDate } from "@app/util/parsers";

import {
  getOrgTimeZone,
  getSelectedDate,
  getSelectedDatePeriod,
  getVisibleCheckInGroups,
  getVisibleUserLocations,
} from "../selectors";
import UserLocation from "../types/UserLocation";
import UserLocationCheckInGroup from "../types/UserLocationCheckInGroup";

import PlaybackStatus from "./enums/PlaybackStatus";
import PausedPlayheadPosition from "./types/PausedPlayheadPosition";
import PlayheadTrack from "./types/PlayheadTrack";
import buildPlaybackTimeline from "./utils/buildPlaybackTimeline";
import buildPlayheadTracks from "./utils/buildPlayheadTracks";
import getTimelineTimeMs from "./utils/getTimelineTimeMs";
import interpolateTrackPosition from "./utils/interpolateTrackPosition";

// Stable empty references so idle renders don't churn referential equality.
const EMPTY_VISITED_IDS: ReadonlySet<UserLocation["id"]> = new Set();
const EMPTY_TRACKS: readonly PlayheadTrack[] = [];
const EMPTY_PAUSED_POSITIONS: readonly PausedPlayheadPosition[] = [];

const userLocationsState = (state: RootState) => state.mapLayers.userLocations;

// Not exported — every consumer asks getPlaybackStatus instead.
const getPlayback = createSelector(userLocationsState, ({ playback }) => playback);

// The single playback status selector — derived, never stored; Idle = not playing at progress >= 1.
export const getPlaybackStatus = createSelector(getPlayback, ({ isPlaying, progress }) =>
  isPlaying ? PlaybackStatus.Playing : progress >= 1 ? PlaybackStatus.Idle : PlaybackStatus.Paused
);

// Parse each BE date string ONCE per events-change (memoized). Every other playback selector
// reuses createdAtMs from here, so parseApiDate never runs per frame or per crossing.
const getVisibleEventsWithCreatedAtMs = createSelector(getVisibleUserLocations, (visibleEvents) =>
  visibleEvents.map((event) => ({
    createdAtMs: parseApiDate(event.createdAt).getTime(),
    event,
  }))
);

// Ascending event timestamps for the engine hook: canPlay's first/last ordering check, and crossing
// detection (how many are <= the playhead time). Not a window boundary — see getPlaybackTimeline for
// the shared progress -> instant mapping every consumer uses instead.
export const getSortedVisibleEventTimesMs = createSelector(
  getVisibleEventsWithCreatedAtMs,
  (eventsWithCreatedAtMs) =>
    eventsWithCreatedAtMs.map((entry) => entry.createdAtMs).sort((left, right) => left - right)
);

// The calendar structure of the rail for the selected period. Day spans the rep's active hours; Week
// and Month span the org-tz calendar week / month, which is what puts empty days on the rail instead
// of clipping them off its ends. undefined = no window could be built (see buildPlaybackTimeline).
export const getPlaybackTimeline = createSelector(
  getSelectedDatePeriod,
  getSelectedDate,
  getOrgTimeZone,
  getSortedVisibleEventTimesMs,
  buildPlaybackTimeline
);

// progress (0..1) -> a real wall-clock instant; undefined while Idle or with no window.
const getPlaybackVirtualTimeMs = createSelector(
  getPlaybackStatus,
  getPlayback,
  getPlaybackTimeline,
  (status, playback, timeline) =>
    status === PlaybackStatus.Idle || !timeline
      ? undefined
      : getTimelineTimeMs(timeline, playback.progress)
);

// Every event whose real createdAt is at/behind the playhead. Reused by pins AND lines.
export const getVisitedLocationIds = createSelector(
  getVisibleEventsWithCreatedAtMs,
  getPlaybackVirtualTimeMs,
  (eventsWithCreatedAtMs, virtualTimeMs) => {
    if (virtualTimeMs === undefined) {
      return EMPTY_VISITED_IDS;
    }
    const visitedIds = new Set<UserLocation["id"]>();
    for (const { createdAtMs, event } of eventsWithCreatedAtMs) {
      if (createdAtMs <= virtualTimeMs) {
        visitedIds.add(event.id);
      }
    }
    return visitedIds;
  }
);

// Ids of check-in groups the playhead has reached — these render full-opacity; the rest stay dimmed
// until reached (temporal reveal). A check-in is itself a trail point, so it reveals on the SAME axis as
// the GPS trail — its representative location id entering the updatedAt-based visited set — never on the
// check-in's own checkinAt, which lives on a different clock (a backfilled / offline-synced / edited
// check-in is stamped updatedAt long after the visit) and would brighten the pin at the wrong moment.
// resultEqualityCheck (lodash isEqual on Set contents) keeps this set's reference stable between
// crossings, so the pins restyle only when a group actually crosses — not on every animation frame.
export const getVisitedCheckInGroupIds = createSelector(
  getVisibleCheckInGroups,
  getVisitedLocationIds,
  (groups, visitedIds): ReadonlySet<UserLocationCheckInGroup["id"]> =>
    new Set(
      groups.filter((group) => visitedIds.has(group.locations[0].id)).map((group) => group.id)
    ),
  {
    memoize: lruMemoize,
    memoizeOptions: { resultEqualityCheck: isEqual },
  }
);

// One ordered track per visible rep. Short-circuits to a STABLE empty array while Idle, so opening
// Field View / selecting reps / changing the date does NO track building — the build (reusing
// already-parsed times) happens lazily on the first Play or scrub.
export const getPlayheadTracks = createSelector(
  getPlaybackStatus,
  getVisibleEventsWithCreatedAtMs,
  (status, eventsWithCreatedAtMs): readonly PlayheadTrack[] =>
    status === PlaybackStatus.Idle ? EMPTY_TRACKS : buildPlayheadTracks(eventsWithCreatedAtMs)
);

// One parked avatar per rep while Paused: where each rep's playhead sits at the paused virtual time.
// Reps with no reached point yet are omitted (their gliding playhead would be invisible too).
// Recomputes only on pause/scrub commits — never per animation frame (Playing returns the empty ref).
export const getPausedPlayheadPositions = createSelector(
  getPlaybackStatus,
  getPlayheadTracks,
  getPlaybackVirtualTimeMs,
  (status, tracks, virtualTimeMs): readonly PausedPlayheadPosition[] => {
    if (status !== PlaybackStatus.Paused || virtualTimeMs === undefined) {
      return EMPTY_PAUSED_POSITIONS;
    }
    return tracks.flatMap((playheadTrack) => {
      // Cursor hint 0 (full locate): runs per pause/scrub commit, not per frame, so no hint needed.
      const frame = interpolateTrackPosition(playheadTrack, virtualTimeMs, 0);
      if (!frame.position) {
        return [];
      }
      const reachedPoint = playheadTrack.points[frame.cursor];
      return [
        {
          day: reachedPoint.day,
          position: frame.position,
          reachedAtMs: reachedPoint.timeMs,
          userId: playheadTrack.userId,
        },
      ];
    });
  }
);
